import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Webhook, Send } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const UnaVez = ({ label, valor }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="integracion-una-vez">
    <p className="text-sm font-semibold text-amber-900">{label} (se muestra una sola vez; cópiala ahora)</p>
    <code className="block break-all text-sm" data-testid="integracion-valor">{valor}</code>
  </div>
);

const Clave = ({ st, run, reload }) => {
  const revocar = async () => {
    if (!window.confirm("La app dejará de poder consultar IntiCheck hasta que generes otra clave. ¿Revocar?")) return;
    try {
      await api.delete("/company/integracion/clave");
      toast.success("Clave revocada");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <KeyRound size={18} />
      <span className="text-sm" data-testid="integracion-clave-estado">{st.tiene_clave ? `Clave activa (${st.clave_prefijo}…) desde ${fmtFecha(st.clave_creada)}` : "Sin clave"}</span>
      <Button size="sm" onClick={() => window.confirm(st.tiene_clave ? "La clave actual dejará de funcionar. ¿Generar una nueva?" : "¿Generar la clave?") && run(() => api.post("/company/integracion/clave"), "Clave de API", "clave")} data-testid="integracion-generar-clave">
        {st.tiene_clave ? "Reemplazar clave" : "Generar clave"}
      </Button>
      {st.tiene_clave && <Button size="sm" variant="outline" onClick={revocar} data-testid="integracion-revocar-clave">Revocar</Button>}
    </div>
  );
};

const WebhookForm = ({ st, run, reload }) => {
  const [url, setUrl] = useState(st.webhook_url || "");
  const guardar = async () => {
    try {
      const { data } = await api.put("/company/integracion/webhook", { url });
      if (data.secreto) run(async () => ({ data }), "Secreto del webhook", "secreto");
      else toast.success("URL guardada");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const probar = async () => {
    try {
      const { data } = await api.post("/company/integracion/webhook/prueba");
      data.ok ? toast.success(`Webhook de prueba entregado (HTTP ${data.http_status})`) : toast.error(`No se entregó: ${data.error}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Webhook size={18} />
        <Input className="max-w-md" placeholder="https://tu-app.cl/webhooks/inticheck" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="integracion-webhook-input" />
        <Button size="sm" disabled={!url.startsWith("https://")} onClick={guardar} data-testid="integracion-webhook-guardar">Guardar URL de webhook</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 pl-7">
        <span className="text-sm text-slate-600" data-testid="integracion-secreto-estado">{st.tiene_secreto ? "Secreto del webhook generado" : "Sin secreto del webhook"}</span>
        <Button size="sm" variant="outline" onClick={() => (!st.tiene_secreto || window.confirm("El secreto actual dejará de servir. ¿Generar uno nuevo?")) && run(() => api.post("/company/integracion/webhook/secreto"), "Secreto del webhook", "secreto")} data-testid="integracion-generar-secreto">
          {st.tiene_secreto ? "Generar nuevo secreto" : "Generar secreto del webhook"}
        </Button>
        <Button size="sm" variant="outline" disabled={!st.webhook_url || !st.tiene_secreto} onClick={probar} data-testid="integracion-webhook-prueba"><Send size={14} className="mr-1" /> Enviar webhook de prueba</Button>
        {st.pendientes > 0 && <span className="text-sm text-amber-700" data-testid="integracion-pendientes">{st.pendientes} avisos pendientes de entrega</span>}
      </div>
    </div>
  );
};

export const IntegracionCard = ({ onChange }) => {
  const [st, setSt] = useState(null);
  const [nuevo, setNuevo] = useState(null);
  const load = () => api.get("/company/integracion").then((r) => { setSt(r.data); onChange?.(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, label, campo) => {
    try {
      const { data } = await fn();
      setNuevo({ label, valor: data[campo] });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!st) return null;
  return (
    <section className="rounded-xl border bg-white p-5 space-y-4" data-testid="integracion-card">
      <h2 className="font-heading text-lg font-semibold">Credenciales</h2>
      <p className="text-sm text-slate-600">La app usa la clave en el header <code>X-API-Key</code>. Cada clave solo ve los clientes de tu empresa y todo uso queda en la bitácora.</p>
      <Clave st={st} run={run} reload={load} />
      <WebhookForm key={st.webhook_url || "nuevo"} st={st} run={run} reload={load} />
      <p className="text-sm text-slate-600" data-testid="integracion-limite">Límite mensual para clientes con aprobación provisional: <b>${Number(st.limite_provisional_clp).toLocaleString("es-CL")}</b> (se cambia en Configuración → «Límite mensual de envíos (CLP)…»).</p>
      {nuevo && <UnaVez {...nuevo} />}
    </section>
  );
};
